import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '@/hooks/useNotifications'
import { useAdminNews } from '@/hooks/useAdmin'
import { supabase } from '@/lib/supabase'
import type { News } from '@/lib/supabase'
import RichContentEditor from './RichContentEditor'

const DEFAULT_BREAKING_SPEED_SECONDS = 75
const MIN_BREAKING_SPEED_SECONDS = 20
const MAX_BREAKING_SPEED_SECONDS = 180

function clampBreakingSpeed(value: number) {
  return Math.min(MAX_BREAKING_SPEED_SECONDS, Math.max(MIN_BREAKING_SPEED_SECONDS, value))
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    publicado: 'bg-green-50 text-green-700 border-green-200',
    rascunho: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    arquivado: 'bg-gray-50 text-gray-700 border-gray-200',
  }
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs border ${colors[status] || colors.rascunho}`}>
      {status === 'publicado' ? 'Publicado' : status === 'rascunho' ? 'Rascunho' : 'Arquivado'}
    </span>
  )
}

function toDatetimeLocal(value?: string | null) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
  return localDate.toISOString().slice(0, 16)
}

function fromDatetimeLocal(value: string) {
  return value ? new Date(value).toISOString() : null
}

function uniqueCategories(values: string[]) {
  return Array.from(new Set(values.filter(Boolean)))
}

function BreakingNewsSettings() {
  const [speedSeconds, setSpeedSeconds] = useState(DEFAULT_BREAKING_SPEED_SECONDS)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const lastSavedSpeedRef = useRef(DEFAULT_BREAKING_SPEED_SECONDS)

  useEffect(() => {
    async function loadSettings() {
      const { data } = await supabase
        .from('site_settings')
        .select('value')
        .eq('key', 'breaking_news')
        .maybeSingle()

      const savedSpeed = clampBreakingSpeed(Number(data?.value?.speedSeconds) || DEFAULT_BREAKING_SPEED_SECONDS)
      lastSavedSpeedRef.current = savedSpeed
      setSpeedSeconds(savedSpeed)
      setLoaded(true)
    }

    loadSettings()
  }, [])

  const updateSpeed = (value: number) => {
    setSpeedSeconds(clampBreakingSpeed(value || DEFAULT_BREAKING_SPEED_SECONDS))
    setSaved(false)
  }

  const saveSettings = async (nextSpeed = speedSeconds) => {
    setSaving(true)

    const { error } = await supabase
      .from('site_settings')
      .upsert({
        key: 'breaking_news',
        value: { speedSeconds: nextSpeed },
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' })

    setSaving(false)

    if (error) {
      alert('Erro ao salvar velocidade: ' + error.message)
      return
    }

    lastSavedSpeedRef.current = nextSpeed
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  useEffect(() => {
    if (!loaded || speedSeconds === lastSavedSpeedRef.current) return

    const timeout = window.setTimeout(() => {
      saveSettings(speedSeconds)
    }, 500)

    return () => window.clearTimeout(timeout)
  }, [loaded, speedSeconds])

  return (
    <div className="bg-white rounded-xl border border-alvinegro-border p-5 mb-6">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-alvinegro-black">Breaking News</h3>
          <p className="text-sm text-alvinegro-muted mt-1">
            Controle a velocidade da faixa de noticias urgentes.
          </p>
        </div>

        {saving ? (
          <span className="px-3 py-1 rounded-full bg-alvinegro-light text-alvinegro-dark border border-alvinegro-border text-xs font-medium w-fit">
            Salvando
          </span>
        ) : saved && (
          <span className="px-3 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-medium w-fit">
            Salvo
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-4 md:items-end">
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-alvinegro-dark">
              Velocidade do ticker
            </label>
            <span className="text-sm font-bold text-alvinegro-black">{speedSeconds}s</span>
          </div>

          <input
            type="range"
            min={MIN_BREAKING_SPEED_SECONDS}
            max={MAX_BREAKING_SPEED_SECONDS}
            step="5"
            value={speedSeconds}
            onChange={(event) => updateSpeed(Number(event.target.value))}
            className="w-full accent-alvinegro-black"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-alvinegro-dark mb-1">
            Segundos
          </label>
          <input
            type="number"
            min={MIN_BREAKING_SPEED_SECONDS}
            max={MAX_BREAKING_SPEED_SECONDS}
            value={speedSeconds}
            onChange={(event) => updateSpeed(Number(event.target.value))}
            className="w-28 px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
          />
        </div>

        <div className="hidden md:block text-xs text-alvinegro-muted pb-2">
          Salva automaticamente
        </div>
      </div>

      <p className="text-xs text-alvinegro-muted mt-3">
        Quanto maior o numero, mais devagar o texto passa. A home atualiza automaticamente em ate 30 segundos.
      </p>
    </div>
  )
}

function NewsForm({ news, onSave, onCancel }: { news?: News | null, onSave: (data: Partial<News>) => void, onCancel: () => void }) {
  const [form, setForm] = useState({
    title: news?.title || '',
    subtitle: news?.subtitle || '',
    content: news?.content || '',
    image_url: news?.image_url || '',
    category: news?.category || 'Geral',
    categories: uniqueCategories(news?.categories?.length ? news.categories : [news?.category || 'Geral']),
    tags: news?.tags?.join(', ') || '',
    author: news?.author || '',
    source: news?.source || '',
    source_url: news?.source_url || '',
    meta_description: news?.meta_description || '',
    slug: news?.slug || '',
    status: (news?.status || 'rascunho') as News['status'],
    published_at: toDatetimeLocal(news?.published_at),
    is_featured: news?.is_featured || false,
    is_headline: news?.is_headline || false,
    is_breaking: news?.is_breaking || false,
  })

  const categories = ['Geral', 'Futebol', 'Internacional', 'Brasileirão', 'Mercado da Bola', 'Institucional', 'Sócio-Torcedor']

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const publishedAt = form.published_at ? fromDatetimeLocal(form.published_at) : null
    const selectedCategories = uniqueCategories([form.category, ...form.categories])
    const data: Partial<News> = {
      ...form,
      categories: selectedCategories,
      tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      slug: form.slug || form.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      published_at: publishedAt,
    }
    if (!data.published_at && data.status === 'publicado') {
      data.published_at = new Date().toISOString()
    }
    onSave(data)
  }

  const toggleCategory = (category: string) => {
    const nextCategories = form.categories.includes(category)
      ? form.categories.filter((item) => item !== category)
      : [...form.categories, category]

    setForm({
      ...form,
      categories: uniqueCategories([form.category, ...nextCategories]),
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 py-6 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-2xl p-6 md:p-8 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif font-bold text-xl text-alvinegro-black">
            {news ? 'Editar Notícia' : 'Nova Notícia'}
          </h3>
          <button onClick={onCancel} className="w-8 h-8 rounded-full bg-alvinegro-light flex items-center justify-center cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Título</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Subtítulo</label>
            <input
              value={form.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Conteúdo</label>
            <RichContentEditor
              value={form.content}
              onChange={(content) => setForm({ ...form, content })}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Imagem URL</label>
              <input
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                placeholder="https://..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Categoria</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value, categories: uniqueCategories([e.target.value, ...form.categories]) })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black bg-white"
              >
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-2">Categorias da materia</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg border border-alvinegro-border bg-alvinegro-light/30 p-3">
              {categories.map((category) => (
                <label key={category} className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.categories.includes(category)}
                    onChange={() => toggleCategory(category)}
                    disabled={category === form.category}
                    className="w-4 h-4 rounded border-alvinegro-border"
                  />
                  <span className={category === form.category ? 'font-medium text-alvinegro-black' : 'text-alvinegro-dark'}>
                    {category}
                  </span>
                </label>
              ))}
            </div>
            <p className="mt-1 text-xs text-alvinegro-muted">
              A categoria principal fica marcada automaticamente. Marque outras para a noticia aparecer em mais secoes.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Autor</label>
              <input
                value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Slug</label>
              <input
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
                placeholder="gerado-automaticamente"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Tags (separadas por vírgula)</label>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              placeholder="botafogo, futebol, internacional"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-alvinegro-dark mb-1">Meta Description</label>
            <input
              value={form.meta_description}
              onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
              className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value as News['status'] })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black bg-white"
              >
                <option value="rascunho">Rascunho</option>
                <option value="publicado">Publicado</option>
                <option value="arquivado">Arquivado</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-alvinegro-dark mb-1">Publicar em</label>
              <input
                type="datetime-local"
                value={form.published_at}
                onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
              />
              <p className="mt-1 text-xs text-alvinegro-muted">
                Para agendar, deixe como Publicado e escolha uma data futura.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-6 py-2">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_featured}
                onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                className="w-4 h-4 rounded border-alvinegro-border"
              />
              Destaque
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_headline}
                onChange={(e) => setForm({ ...form, is_headline: e.target.checked })}
                className="w-4 h-4 rounded border-alvinegro-border"
              />
              Manchete
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_breaking}
                onChange={(e) => setForm({ ...form, is_breaking: e.target.checked })}
                className="w-4 h-4 rounded border-alvinegro-border"
              />
              Breaking News
            </label>
          </div>
          <div className="flex items-center gap-3 pt-4">
            <button
              type="submit"
              className="bg-alvinegro-black text-white px-6 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
            >
              {news ? 'Salvar Alterações' : 'Criar Notícia'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function NewsManager() {
  const navigate = useNavigate()
  const { news, loading, totalCount, fetchNews, createNews, updateNews, deleteNews, publishNews, unpublishNews } = useAdminNews()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [editNews, setEditNews] = useState<News | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const { sendNotification } = useNotifications()
  const [sendNotificationConfirm, setSendNotificationConfirm] = useState<News | null>(null)
  const perPage = 10

  const showToast = (message: string, type: 'success' | 'error' = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  useEffect(() => {
    fetchNews(page, perPage, search, statusFilter)
  }, [page, search, statusFilter])

  const handleSave = async (data: Partial<News>) => {
    setActionLoading(true)
    if (editNews) {
      const result = await updateNews(editNews.id, data)
      if (result) {
        showToast('Notícia atualizada com sucesso!')
        setShowForm(false)
        setEditNews(null)
      } else {
        showToast('Erro ao atualizar notícia', 'error')
      }
    } else {
      const result = await createNews(data)
      if (result) {
        showToast('Notícia criada com sucesso!')
        setShowForm(false)
      } else {
        showToast('Erro ao criar notícia', 'error')
      }
    }
    setActionLoading(false)
    fetchNews(page, perPage, search, statusFilter)
  }

  const handleDelete = async (id: string) => {
    setActionLoading(true)
    const success = await deleteNews(id)
    if (success) {
      showToast('Notícia excluída')
      setDeleteConfirm(null)
    } else {
      showToast('Erro ao excluir', 'error')
    }
    setActionLoading(false)
    fetchNews(page, perPage, search, statusFilter)
  }

  const handlePublish = async (id: string) => {
    setActionLoading(true)
    const result = await publishNews(id)
    if (result) {
      showToast('Notícia publicada!')
    } else {
      showToast('Erro ao publicar', 'error')
    }
    setActionLoading(false)
    fetchNews(page, perPage, search, statusFilter)
  }

  const handleUnpublish = async (id: string) => {
    setActionLoading(true)
    const result = await unpublishNews(id)
    if (result) {
      showToast('Notícia despublicada')
    } else {
      showToast('Erro ao despublicar', 'error')
    }
    setActionLoading(false)
    fetchNews(page, perPage, search, statusFilter)
  }

  const handleSendNotification = async (newsItem: News) => {
    setActionLoading(true)
    const result = await sendNotification({
      title: newsItem.title,
      body: newsItem.subtitle || newsItem.content.slice(0, 120) + '...',
      image_url: newsItem.image_url || undefined,
      link: `/noticias/${newsItem.slug}`,
      type: 'breaking',
    })
    if (result) {
      showToast('Notificação enviada com sucesso!')
    } else {
      showToast('Erro ao enviar notificação', 'error')
    }
    setSendNotificationConfirm(null)
    setActionLoading(false)
  }

  const totalPages = Math.ceil(totalCount / perPage)

  return (
    <div>
      <BreakingNewsSettings />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-sm text-alvinegro-muted"></i>
            <input
              type="text"
              placeholder="Buscar notícias..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              className="w-full pl-9 pr-4 py-2 rounded-full border border-alvinegro-border text-sm focus:outline-none focus:border-alvinegro-black"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1) }}
            className="px-4 py-2 rounded-full border border-alvinegro-border text-sm bg-white focus:outline-none focus:border-alvinegro-black"
          >
            <option value="">Todos os status</option>
            <option value="publicado">Publicado</option>
            <option value="rascunho">Rascunho</option>
            <option value="arquivado">Arquivado</option>
          </select>
        </div>
        <button
          onClick={() => { setEditNews(null); setShowForm(true) }}
          className="flex items-center gap-2 bg-alvinegro-black text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-alvinegro-dark transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line"></i>
          Nova Notícia
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-alvinegro-border overflow-hidden">
        {loading ? (
          <div className="p-8 space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-16 h-12 rounded bg-alvinegro-light" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-alvinegro-light rounded w-1/4" />
                  <div className="h-3 bg-alvinegro-light rounded w-full" />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-alvinegro-border bg-alvinegro-light">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Notícia</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden md:table-cell">Categoria</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Status</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Data</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider hidden sm:table-cell">Views</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-alvinegro-muted uppercase tracking-wider">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {news.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-12 text-center text-alvinegro-muted">
                        <i className="ri-article-line text-4xl mb-3 block"></i>
                        <p className="text-sm">Nenhuma notícia encontrada</p>
                      </td>
                    </tr>
                  ) : (
                    news.map((item) => (
                      <tr key={item.id} className="border-b border-alvinegro-border hover:bg-alvinegro-light/50 transition-colors">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            {item.image_url && (
                              <div className="w-16 h-12 rounded-lg bg-alvinegro-light overflow-hidden shrink-0 hidden sm:block">
                                <img src={item.image_url} alt="" className="w-full h-full object-cover" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-alvinegro-black truncate max-w-[200px] md:max-w-[300px]">{item.title}</p>
                              <div className="flex items-center gap-2 mt-1">
                                {item.is_headline && <span className="text-xs text-alvinegro-gold">Manchete</span>}
                                {item.is_featured && <span className="text-xs text-alvinegro-muted">Destaque</span>}
                                {item.is_breaking && <span className="text-xs text-red-600">Breaking</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell">
                          <span className="text-xs text-alvinegro-muted">
                            {item.categories?.length ? item.categories.join(', ') : item.category}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={item.status} />
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs text-alvinegro-muted">
                            {item.published_at ? new Date(item.published_at).toLocaleDateString('pt-BR') : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden sm:table-cell">
                          <span className="text-xs text-alvinegro-muted">{item.views || 0}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {item.is_breaking && item.status === 'publicado' && (
                              <button
                                onClick={() => setSendNotificationConfirm(item)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                                title="Enviar Notificação"
                              >
                                <i className="ri-notification-3-line text-sm"></i>
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/noticias/${item.slug}`)}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-muted transition-colors cursor-pointer"
                              title="Ver"
                            >
                              <i className="ri-eye-line text-sm"></i>
                            </button>
                            {item.status !== 'publicado' ? (
                              <button
                                onClick={() => handlePublish(item.id)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-green-50 text-green-600 transition-colors cursor-pointer"
                                title="Publicar"
                              >
                                <i className="ri-check-line text-sm"></i>
                              </button>
                            ) : (
                              <button
                                onClick={() => handleUnpublish(item.id)}
                                disabled={actionLoading}
                                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-yellow-50 text-yellow-600 transition-colors cursor-pointer"
                                title="Despublicar"
                              >
                                <i className="ri-draft-line text-sm"></i>
                              </button>
                            )}
                            <button
                              onClick={() => { setEditNews(item); setShowForm(true) }}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light text-alvinegro-dark transition-colors cursor-pointer"
                              title="Editar"
                            >
                              <i className="ri-pencil-line text-sm"></i>
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(item.id)}
                              disabled={actionLoading}
                              className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-red-50 text-red-600 transition-colors cursor-pointer"
                              title="Excluir"
                            >
                              <i className="ri-delete-bin-line text-sm"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-alvinegro-border">
                <p className="text-xs text-alvinegro-muted">
                  Mostrando {Math.min((page - 1) * perPage + 1, totalCount)}-{Math.min(page * perPage, totalCount)} de {totalCount}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-left-s-line text-sm"></i>
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    const pageNum = i + Math.max(1, Math.min(page - 2, totalPages - 4))
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum)}
                        className={`w-8 h-8 rounded-full text-sm font-medium transition-colors cursor-pointer ${
                          pageNum === page ? 'bg-alvinegro-black text-white' : 'hover:bg-alvinegro-light text-alvinegro-dark'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  })}
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-alvinegro-light transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <i className="ri-arrow-right-s-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <NewsForm news={editNews} onSave={handleSave} onCancel={() => { setShowForm(false); setEditNews(null) }} />
      )}

      {/* Delete Confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-delete-bin-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Excluir notícia</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-6">Tem certeza que deseja excluir esta notícia permanentemente?</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleDelete(deleteConfirm)} disabled={actionLoading} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50">{actionLoading ? 'Excluindo...' : 'Excluir'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Send Notification Confirm */}
      {sendNotificationConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                <i className="ri-notification-3-line text-red-600 text-lg"></i>
              </div>
              <h3 className="font-semibold text-alvinegro-black">Enviar Notificação</h3>
            </div>
            <p className="text-sm text-alvinegro-muted mb-2">Enviar notificação push para todos os usuários sobre esta notícia:</p>
            <p className="text-sm font-medium text-alvinegro-black mb-6">{sendNotificationConfirm.title}</p>
            <div className="flex items-center gap-3">
              <button onClick={() => setSendNotificationConfirm(null)} className="flex-1 py-2.5 rounded-full border border-alvinegro-border text-sm font-medium text-alvinegro-dark hover:bg-alvinegro-light transition-colors cursor-pointer">Cancelar</button>
              <button onClick={() => handleSendNotification(sendNotificationConfirm)} disabled={actionLoading} className="flex-1 py-2.5 rounded-full bg-red-500 text-white text-sm font-medium hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50">{actionLoading ? 'Enviando...' : 'Enviar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50">
          <div className={`rounded-xl px-5 py-3 shadow-lg border text-sm font-medium flex items-center gap-2 ${toast.type === 'success' ? 'bg-white border-green-200 text-green-700' : 'bg-white border-red-200 text-red-600'}`}>
            <i className={`${toast.type === 'success' ? 'ri-check-double-line' : 'ri-error-warning-line'}`}></i>
            {toast.message}
          </div>
        </div>
      )}
    </div>
  )
}
